/**
 * Entry point / app selector.
 *
 * Two front-ends share this bundle and this HTML shell:
 *   /app/*  -> the mobile PWA (src/pwa/*), whose UI follows
 *              E2M_Native_App_Design.md. It reuses this repo's Vuex store,
 *              Firebase instance and auth endpoint — no forked business logic.
 *   /*      -> the classic OnAir web app (src/legacy-bootstrap.ts), untouched.
 *
 * The chosen bootstrap is loaded dynamically so the other front-end's
 * dependencies are never evaluated: the PWA does not pull in jQuery, Bootstrap,
 * Vuetify or the global mixins, and the classic app is byte-for-byte what it was.
 *
 * `bus` stays exported here because the classic app imports it from `@/main` in
 * ~40 places.
 */
import { Emitter } from './EventBus/EventBus'

// Global event bus (replaces `export const bus = new Vue()`).
export const bus = new Emitter()

// PWA: rebrands the web app manifest with the active event's name/logo/colour.
// Side-effect only — renders nothing and changes no app behaviour.
import './pwa-manifest'

const PWA_PATH = /^\/app(\/|$)/

/**
 * Installed shortcuts launched outside /app/* get sent to the mobile UI.
 *
 * A PWA's `start_url` is captured when the user installs and does NOT change when
 * the manifest is updated, so shortcuts created before the mobile UI existed still
 * open the classic desktop app — unusable in a phone-sized window. This rescues
 * them without a reinstall.
 *
 * Only fires on the launch navigation: the flag means "Open the web app" inside
 * the PWA can still reach classic pages in the same window.
 */
function redirectStandaloneLaunch(): boolean {
	if (typeof window === 'undefined') return false
	if (PWA_PATH.test(window.location.pathname)) return false

	const standalone =
		(typeof window.matchMedia === 'function' &&
			['standalone', 'minimal-ui', 'fullscreen'].some(
				(mode) => window.matchMedia('(display-mode: ' + mode + ')').matches
			)) ||
		(window.navigator as any).standalone === true
	if (!standalone) return false

	const LAUNCHED = 'pwa-launched'
	try {
		if (sessionStorage.getItem(LAUNCHED)) return false
		sessionStorage.setItem(LAUNCHED, '1')
	} catch (e) {
		// Storage blocked — better to redirect once than not at all.
	}

	window.location.replace('/app/home')
	return true
}

if (redirectStandaloneLaunch()) {
	// Navigating away; don't boot either app into a page that's about to unload.
} else if (typeof window !== 'undefined' && PWA_PATH.test(window.location.pathname)) {
	// Normalise the bare "/app" so the installed app always starts on a real route.
	if (window.location.pathname === '/app' || window.location.pathname === '/app/') {
		window.history.replaceState(null, '', '/app/home' + window.location.search)
	}
	import('./pwa/bootstrap')
} else {
	import('./legacy-bootstrap')
}
